<template>

    <transition name="dropdown">

        <div v-if="toggle && target" class="var-selector" :style="styles">

            <div class="var-selector-body">

                <v-alert class="mb-10" size="sm" :message="error" />

                <v-form-group dir="ver">
                    <v-select size="sm" :model="params.field" @input="$set(params, 'field', $event)">
                        <option value="">Select Input</option>
                        <optgroup label="Customer Traits">
                            <option v-for="t in traits" :key="t.id" :value="'customer.'+t.field">{{ t.name }}</option>
                        </optgroup>
                        <optgroup v-if="triggerProperties.length" label="Trigger Properties">
                            <option v-for="p in triggerProperties" :key="p.id" :value="p.field">{{ p.name }}</option>
                        </optgroup>
                    </v-select>
                </v-form-group>

                <v-form-group v-if="params.field" dir="ver">
                    <v-select size="sm" :model="params.filter" @input="params.filter = $event">
                        <option value="">Select Filter</option>
                        <option value="">None</option>
                        <optgroup label="Text">
                            <option value="capitalize">Capitalize</option>
                            <option value="downcase">Lowercase</option>
                            <option value="strip">Trim Whitespace</option>
                            <option value="upcase">Uppercase</option>
                        </optgroup>
                        <optgroup label="Number">
                            <option value="plus: 2">Add</option>
                            <option :value="`number_to_currency: '${project.currency}`">Currency</option>
                            <option value="divided_by: 2">Divide</option>
                            <option value="number_to_formatted">Formatted</option>
                            <option value="times: 2">Multiply</option>
                            <option value="round">Round</option>
                            <option value="minus: 2">Subtract</option>
                        </optgroup>
                        <optgroup label="Date">
                            <option value="date: '%b %d, %Y'">Date</option>
                            <option value="date: '%Y-%m-%d %H:%M'">Date &amp; Time</option>
                        </optgroup>
                    </v-select>
                </v-form-group>

                <v-form-group v-if="params.field" dir="ver">
                    <v-input size="sm" placeholder="Default Value" :model="params.default" @input="params.default = $event" />
                </v-form-group>

            </div>

            <div class="var-selector-footer">
                <v-button size="xs" btn-style="white" @click.native="close">Cancel</v-button>
                <v-button size="xs" @click.native="insert">Insert</v-button>
            </div>

        </div>

    </transition>

</template>

<style scoped lang="scss">

    .var-selector {
        @include shad(2);
        background: #fff;
        border: 1px solid $color-rule;
        border-radius: 5px;
        left: 0;
        overflow: hidden;
        position: fixed;
        top: 0;
        width: 300px;
        z-index: 1000;
        .var-selector-body {
            padding: 15px;
            .form-group {
                margin: 0 0 10px 0;
                &:last-child {
                    margin-bottom: 0;
                }
            }
        }
        .var-selector-footer {
            display: flex;
            justify-content: space-between;
            padding: 0 15px 15px 15px;
        }
    }

</style>

<script>

export default {

    props: {
        toggle: {
            type: HTMLElement,
        },
        target: {
            type: HTMLElement,
        },
    },

    data() {
        return {
            error: null,
            selectedTarget: null,
            range: null,
            styles: {},
            params: {},
        }
    },

    computed: {

        campaign() {
            return this.$store.getters['campaign/campaign'];
        },

        traits() {
            let traits = this.project.traits || [];
            return traits.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
        },

        triggerProperties() {
            let trigger = this.campaign.trigger;
            if (!trigger || !trigger.type) return [];
            if (trigger.type.startsWith('event')) {
                let event = this.project.events.find(i => i.id === trigger.event_id);
                if (event) {
                    return event.properties.map(i => ({
                        field: `event.${i.name}`,
                        name: i.name
                    }));
                }
            } else if (trigger.type.startsWith('segment')) {
                return [{
                    field: `segment.name`,
                    name: `Segment Name`,
                }];
            } else if (trigger.type.startsWith('tag')) {
                return [{
                    field: `tag.name`,
                    name: `Tag Name`,
                }];
            }
            return [];
        },

    },

    watch: {

        toggle(val) {
            this.error = null;
            this.params = {};
            if (val) {
                this.selectedTarget = window.getSelection().anchorNode?.parentNode;
                this.range = window.getSelection().rangeCount >= 1 ? window.getSelection().getRangeAt(0) : null;
                let rectToggle = this.toggle.getBoundingClientRect();
                let x = rectToggle.left + rectToggle.width - 300;
                let y = rectToggle.top + rectToggle.height + 2;
                this.styles = { left: x + 'px', top: y + 'px' };
            }
        },

    },

    methods: {

        insert() {
            // Validate.
            if (!this.params.field) {
                this.error = 'Select a variable to insert.';
                return;
            }
            // Create markup.
            let str = '';
            str += `{{ ${this.params.field}`;
            str += this.params.filter ? ` | ${this.params.filter}` : '';
            str += this.params.default ? ` | default: '${this.params.default}'` : '';
            str += ' }} ';
            // Add markup.
            if (['INPUT', 'TEXTAREA'].includes(this.target.tagName)) {
                this.target.focus();
                document.execCommand('insertText', false, str);
            } else if (this.range && this.selectedTarget === this.target) {
                let sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(this.range);
                sel.getRangeAt(0).insertNode(document.createTextNode(str));
                sel.empty();
            } else {
                this.target.appendChild(document.createTextNode(str));
            }
            this.close();
        },

        close() {
            this.$emit('close');
        },

    }

}

</script>
